import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { PublicUser } from '@shared/types';

export function Modal({
  title,
  subtitle,
  onClose,
  children,
  footer,
  wide = false,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    ref.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={wide ? 'modal modal-wide' : 'modal'}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={ref}
      >
        <div className="modal-head">
          <div>
            <h2>{title}</h2>
            {subtitle ? (
              <p className="small muted" style={{ margin: '4px 0 0' }}>
                {subtitle}
              </p>
            ) : null}
          </div>
          <button className="btn-quiet" onClick={onClose} aria-label="Schließen">
            ✕
          </button>
        </div>
        {children}
        {footer ? <div className="modal-foot">{footer}</div> : null}
      </div>
    </div>
  );
}

export function CopyButton({
  value,
  label = 'Kopieren',
  disabled = false,
}: {
  value: string;
  label?: string;
  disabled?: boolean;
}): JSX.Element {
  const [done, setDone] = useState(false);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      setDone(true);
      window.setTimeout(() => setDone(false), 1600);
    } catch {
      // Ohne HTTPS gibt der Browser die Zwischenablage nicht frei.
      window.prompt('Zum Kopieren markieren und Cmd+C drücken:', value);
    }
  }, [value]);

  return (
    <button className="btn-sm" onClick={copy} disabled={disabled || !value}>
      {done ? '✓ Kopiert' : label}
    </button>
  );
}

export function initialsOf(user: Pick<PublicUser, 'name' | 'email'>): string {
  const initials = (user.name || user.email)
    .split(/[\s.@]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
  return initials || '?';
}

/**
 * Profilbild oder Initialen.
 *
 * Nicht jedes Google-Konto liefert ein Bild, und manche Bild-Adressen von
 * Google laufen nach einer Weile ins Leere. Beides darf nicht zu einem kaputten
 * Bildsymbol fuehren - schlaegt das Laden fehl, uebernehmen die Initialen.
 */
export function Avatar({
  user,
  size = 26,
}: {
  user: Pick<PublicUser, 'name' | 'email' | 'picture'>;
  size?: number;
}): JSX.Element {
  const [broken, setBroken] = useState(false);
  const style = { width: size, height: size, fontSize: Math.round(size * 0.42) };

  if (user.picture && !broken) {
    return (
      <img
        className="avatar"
        src={user.picture}
        alt=""
        style={style}
        referrerPolicy="no-referrer"
        onError={() => setBroken(true)}
      />
    );
  }
  return (
    <span className="avatar" style={style}>
      {initialsOf(user)}
    </span>
  );
}

export function ErrorNote({ error }: { error: string | null }): JSX.Element | null {
  if (!error) return null;
  return <div className="note note-danger">{error}</div>;
}

/**
 * Kapselt "läuft gerade" plus Fehlermeldung fuer Aktionen mit Serveraufruf.
 * Spart in jeder Ansicht zwei useState und einen try/catch.
 */
export function useAction(): {
  busy: boolean;
  error: string | null;
  setError: (msg: string | null) => void;
  run: <T>(fn: () => Promise<T>) => Promise<T | undefined>;
} {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
    setBusy(true);
    setError(null);
    try {
      return await fn();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return undefined;
    } finally {
      setBusy(false);
    }
  }, []);

  return { busy, error, setError, run };
}

export function Confirm({
  title,
  message,
  confirmLabel = 'Bestätigen',
  danger = false,
  onConfirm,
  onClose,
}: {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  onConfirm: () => Promise<void> | void;
  onClose: () => void;
}): JSX.Element {
  const { busy, error, run } = useAction();
  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} disabled={busy}>
            Abbrechen
          </button>
          <button
            className={danger ? 'btn-danger' : 'btn-primary'}
            disabled={busy}
            onClick={async () => {
              const ok = await run(async () => {
                await onConfirm();
                return true;
              });
              if (ok) onClose();
            }}
          >
            {busy ? 'Moment …' : confirmLabel}
          </button>
        </>
      }
    >
      <ErrorNote error={error} />
      <div className="small">{message}</div>
    </Modal>
  );
}
